import qs from "qs";
import React, { useEffect, useMemo, useState } from "react";
import { Button, DownloadIcon } from "@pd/pd-ui-kit";
import { Flex } from "antd";
import { useForm } from "react-hook-form/dist/index.ie11";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";

import Preloader from "~/components/Preloader";
import DynamicTable from "~/pages/StaffBonus/DynamicTable";
import StaffBonusFilter from "~/pages/StaffBonus/Filter";
import useGetStaffBonus from "~/pages/StaffBonus/hooks/useGetStaffBonus";
import useGetExpectations from "~/pages/StaffBonus/hooks/useGetStaffExpectations";
import useUpdateStaffBonusColumn from "~/pages/StaffBonus/hooks/useUpdateStaffBonusColumn";
import useGetStaffBonusColumns from "~/pages/StaffBonus/useGetStaffBonusColumns";
import Wrapper from "~/shared/ui/Wrapper";
import { useGetLocationData } from "~/hooks/locationData";
import { FILTER_BY } from "~/pages/Staff/assets/StaffFilter/lib";
import { getDataStaffBonusReport } from "~/pages/StaffBonus/GetDataStaffRequests/getDataStaffBonusReport";
import { COLUMNS_DATA } from "~/pages/StaffBonus/options";
import { usePermissions } from "~/shared/context/PermissionContext";
import { useNotify } from "~/shared/hooks/useNotify";
import { TableV2 } from "~/shared/ui/TableV2";

import { getStaffBonusColumnsColumns } from "./model/tableAdapter";

import s from "./StaffBonus.less";

const parseUrlList = (raw: unknown): string[] =>
  raw ? String(raw).split(",").filter(Boolean) : [];

const StaffBonus = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const [urlParamsReport, setUrlParamsReport] = useState<string>("");
  const [queryEnabled, setQueryEnabled] = useState(false);

  // Ключи фильтра приходят из URL и не описаны в типе useGetLocationData
  const { filterBy, filterItems, filterExpectations, period } =
    useGetLocationData() as Record<string, unknown>;

  // Query-строка запроса бонусов — из URL-фильтра (раньше собиралась
  // buildDealerGroupQuery из состояния формы StaffFilter)
  const bonusQueryParams = useMemo(() => {
    const dealerGroup = {
      ids: [] as number[],
      cities: [] as string[],
      FOs: [] as string[],
      countries: [] as string[],
      subjects: [] as string[],
    };

    const values = parseUrlList(filterItems);
    if (filterBy && values.length) {
      switch (filterBy) {
        case FILTER_BY.CITY:
          dealerGroup.cities = values;
          break;
        case FILTER_BY.FO:
          dealerGroup.FOs = values;
          break;
        case FILTER_BY.AREA:
          dealerGroup.subjects = values;
          break;
        case FILTER_BY.COUNTRY:
          dealerGroup.countries = values;
          break;
        case FILTER_BY.DEALER:
          dealerGroup.ids = values.map(Number);
          break;
      }
    }

    return qs.stringify(
      {
        dealerGroup,
        actualOn: period
          ? String(period)
          : new Date().toISOString().split("T")[0],
        expectationsIds: parseUrlList(filterExpectations).map(Number),
      },
      { allowEmptyArrays: true },
    );
  }, [filterBy, filterItems, filterExpectations, period]);

  const filterReady = Boolean(
    filterBy && period && parseUrlList(filterExpectations).length,
  );

  const { data } = useGetStaffBonus(bonusQueryParams, filterReady);

  const {
    data: dataColumns,
    refetch: refetchColumns,
    isLoading: isLoadingColumns,
  } = useGetStaffBonusColumns();

  const { error, isLoading } = useGetExpectations();

  const { notifyError, notifyReport } = useNotify();

  const {
    data: queryDataReport,
    error: errorReport,
    refetch: refetchReport,
  } = useQuery({
    queryKey: ["getStaffReport", urlParamsReport],
    queryFn: () => getDataStaffBonusReport(urlParamsReport),
    enabled: queryEnabled,
    staleTime: Infinity,
  });

  const getStaffReports = () => {
    if (queryEnabled && urlParamsReport === bonusQueryParams) {
      refetchReport();
      return;
    }
    setUrlParamsReport(bonusQueryParams);
    setQueryEnabled(true);
  };

  useEffect(() => {
    if (queryDataReport) {
      notifyReport(queryDataReport.fileHash);
    }
  }, [queryDataReport]);
  const errorNotification = () => {
    notifyError(t("common.data_uploaded_error_size"));
  };

  useEffect(() => {
    if (error || errorReport) {
      return errorNotification();
    }
  }, [error, errorReport]);

  // «Скачать файл» — выгрузка отчёта по применённому фильтру (из URL)
  const handleDownloadReport = () => {
    getStaffReports();
  };

  const goQReport = () => {
    window.open("/staff/bonus-q-report", "_blank");
  };

  const { control, getValues } = useForm();
  const [openRows, setOpenRows] = useState({});

  const toggleRow = (rowId: string, state?: boolean) => {
    setOpenRows((prev) => ({
      ...prev,
      [rowId]: typeof state === "boolean" ? state : !prev[rowId],
    }));
  };

  const { mutate: updateColumn } = useUpdateStaffBonusColumn();

  const handleSave = (dataObject) => {
    const values = getValues();
    updateColumn(
      {
        id: dataObject.id,
        name: dataObject.name,
        description: values.description || dataObject.description,
      },
      {
        onSuccess: () => {
          refetchColumns();
          toggleRow(dataObject.id, false);
        },
      },
    );
  };

  const handleCancel = (rowId) => {
    toggleRow(rowId, false);
  };

  const columnsTableColumns = getStaffBonusColumnsColumns(
    t,
    COLUMNS_DATA,
    control,
    openRows,
    toggleRow,
    handleSave,
    handleCancel,
  );

  return (
    <Wrapper.Content
      title={t("staff.bonus_title")}
      additionalClassName={s.staff_wrapper}
      filterComponent={<StaffBonusFilter />}
      controlComponent={
        <Flex gap={10}>
          {hasPermission("employeeAccounting.statistics.quarter") && (
            <Button onClick={goQReport} size={"middle"}>
              {t("pkd.reports")}
            </Button>
          )}
        </Flex>
      }
    >
      <Flex justify={"space-between"} className={s.actionsRow}>
        <Flex gap={10}>
          {hasPermission("employeeAccounting.statistics.bonus.report.save") && (
            <Button
              color={"secondary"}
              variant={"filled"}
              bold
              fontSize="12"
              size={"middle"}
              radius={30}
              icon={<DownloadIcon size={16} fill="#495a64" />}
              onClick={handleDownloadReport}
            >
              {t("ui.download_file")}
            </Button>
          )}
        </Flex>
      </Flex>
      <div className={s.wrapper}>
        {data?.table && !isLoading ? (
          <DynamicTable table={data.table} />
        ) : (
          <Preloader />
        )}
      </div>

      {/*<Wrapper.InnerContent>*/}
      <TableV2
        columns={columnsTableColumns}
        dataSource={dataColumns?.columns}
        loading={isLoadingColumns}
        pagination={false}
        onRowEdit={
          hasPermission("employeeAccounting.bonus.columns.update")
            ? (record) => toggleRow(String(record.id))
            : undefined
        }
      />
      {/*</Wrapper.InnerContent>*/}
    </Wrapper.Content>
  );
};

export default StaffBonus;
